"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Check, Bell } from "lucide-react";

/**
 * El pase de wallet del PEDIDO. Deliberadamente NO habla de puntos ni de tarjeta
 * de sellos: `loadRedyPoints` devuelve 0 mientras no haya premio canjeable y
 * `renderPass` omite el campo con 0 — y en prod `redy_rewards` está vacía, así que
 * el pase no muestra puntos a nadie. Lo que sí hace, y es lo que se cuenta aquí,
 * es seguir el pedido en vivo y quedarse después como la tarjeta del cliente.
 *
 * Tampoco dice "tarjeta" como concepto principal: la página de lealtad vende
 * identidad por NÚMERO DE TELÉFONO ("sin tarjeta que cargar") y las dos frases
 * juntas se leerían como una contradicción.
 */

const ESTADOS = [
  { en: "Received", es: "Recibida", done: true },
  { en: "Cooking", es: "Cocinando", done: true },
  { en: "Ready", es: "Lista", done: false, now: true },
  { en: "On the way", es: "En camino", done: false },
  { en: "Delivered", es: "Entregada", done: false },
];

export default function OnlineOrderingWalletScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
                {t("Google Wallet · Apple Wallet", "Google Wallet · Apple Wallet")}
              </div>
              <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal mb-6">
                {t("No app. ", "Sin app. ")}
                <br />
                <span className="text-redy-charcoal/30">{t("No “is it ready?”", "Sin “¿ya está?”")}</span>
              </h2>
              <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mb-6">
                {t(
                  "When they confirm the order, your customer can save it to their phone — Google Wallet on Android, Apple Wallet on iPhone. It moves on its own while you cook, and the phone tells them the moment it's ready. Nothing to install, nothing to refresh.",
                  "Al confirmar el pedido, tu cliente puede guardarlo en su teléfono — Google Wallet en Android, Apple Wallet en iPhone. Avanza solo mientras cocinas, y el teléfono le avisa en cuanto está lista. Nada que instalar, nada que refrescar."
                )}
              </p>
              <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mb-6">
                {t(
                  "When the order is done the pass stays put. Next time they order from you, the same pass comes back to life with the new one.",
                  "Terminado el pedido, el pase se queda ahí. La próxima vez que te pidan, el mismo pase revive con la orden nueva."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-white px-4 py-3 rounded-full border border-black/5">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "rgba(46, 204, 113, 0.14)" }}
                >
                  <Check className="w-3.5 h-3.5" strokeWidth={3} style={{ color: "#2ECC71" }} />
                </div>
                <div className="text-[13px] font-bold text-redy-charcoal">
                  {t("Nothing to turn on. It comes with online ordering.", "No hay nada que encender. Viene con los pedidos en línea.")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: el pase en la pantalla de bloqueo */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="relative bg-white rounded-[28px] border border-black/5 shadow-[0_20px_60px_rgba(0,0,0,0.04)] p-6 md:p-8"
            >
              {/* Aviso del teléfono */}
              <div
                className="rounded-[18px] p-4 mb-6 flex items-start gap-3"
                style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
              >
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <Bell className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black tracking-widest uppercase text-white/75 mb-0.5">
                    {t("Wallet · now", "Wallet · ahora")}
                  </div>
                  <div className="text-[14px] font-black text-white leading-snug">
                    {t("Your order is ready for pickup.", "Tu pedido está listo para recoger.")}
                  </div>
                </div>
              </div>

              {/* El pase */}
              <div className="rounded-[18px] border border-black/5 bg-[#FAFAF8] p-5">
                <div className="flex items-baseline justify-between mb-5">
                  <div>
                    <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
                      {t("Order", "Orden")} #148
                    </div>
                    <div className="text-[16px] font-black text-redy-charcoal">Pepitos · Dallas</div>
                  </div>
                  <div className="text-[20px] font-black tabular-nums text-redy-charcoal">$24.50</div>
                </div>

                <div className="space-y-2.5">
                  {ESTADOS.map((e) => (
                    <div key={e.en} className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                        style={{
                          backgroundColor: e.done
                            ? "#2ECC71"
                            : e.now
                              ? "#F4722B"
                              : "rgba(0,0,0,0.08)",
                        }}
                      >
                        {e.done && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
                      </div>
                      <span
                        className="text-[13px] font-bold"
                        style={{
                          color: e.done || e.now ? undefined : "rgba(0,0,0,0.3)",
                        }}
                      >
                        {t(e.en, e.es)}
                      </span>
                      {e.now && (
                        <span
                          className="text-[10px] font-black tracking-widest uppercase ml-auto"
                          style={{ color: "#F4722B" }}
                        >
                          {t("now", "ahora")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
